import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth";
import { MemberLayout } from "./components/MemberLayout";
import { AdminLayout } from "./components/AdminLayout";
import { LoginPage } from "./pages/Login";
import { RegisterPage } from "./pages/Register";
import { PasswordChangePage } from "./pages/PasswordChange";
const LiffPage = lazy(() =>
  import("./pages/Liff").then((m) => ({ default: m.LiffPage })),
);
import { PasswordResetRequestPage } from "./pages/PasswordResetRequest";
import { PasswordResetConfirmPage } from "./pages/PasswordResetConfirm";
import { HomePage } from "./pages/Home";
import { EventsPage } from "./pages/Events";
import { EventsMinePage } from "./pages/EventsMine";
import { EventDetailPage } from "./pages/EventDetail";
import { ContentsPage } from "./pages/Contents";
import { ContentDetailPage } from "./pages/ContentDetail";
import { ProductsPage } from "./pages/Products";
import { MyPage } from "./pages/MyPage";
import { MyPageProfileEditPage } from "./pages/MyPageProfileEdit";
import { QrPage } from "./pages/Qr";
import { TermsPage } from "./pages/Terms";
import { privacyDoc as PrivacyDoc } from "./legal";
import { LegalLayout } from "./components/LegalLayout";
import { FirstHearingPage } from "./pages/FirstHearing";
// 管理画面は会員が開くことがなく、リッチテキストエディタ等で重い。
// 初回表示（会員サイト）に載せないよう route 単位で遅延読込にする。
const AdminHomePage = lazy(() =>
  import("./pages/admin/AdminHome").then((m) => ({ default: m.AdminHomePage })),
);
const AdminMembersPage = lazy(() =>
  import("./pages/admin/AdminMembers").then((m) => ({ default: m.AdminMembersPage })),
);
const AdminMemberDetailPage = lazy(() =>
  import("./pages/admin/AdminMemberDetail").then((m) => ({ default: m.AdminMemberDetailPage })),
);
const AdminEventsPage = lazy(() =>
  import("./pages/admin/AdminEvents").then((m) => ({ default: m.AdminEventsPage })),
);
const AdminCheckinPage = lazy(() =>
  import("./pages/admin/AdminCheckin").then((m) => ({ default: m.AdminCheckinPage })),
);
const AdminNoticesPage = lazy(() =>
  import("./pages/admin/AdminNotices").then((m) => ({ default: m.AdminNoticesPage })),
);
const AdminContentsPage = lazy(() =>
  import("./pages/admin/AdminContents").then((m) => ({ default: m.AdminContentsPage })),
);
const AdminProductsPage = lazy(() =>
  import("./pages/admin/AdminProducts").then((m) => ({ default: m.AdminProductsPage })),
);
const AdminHearingsPage = lazy(() =>
  import("./pages/admin/AdminHearings").then((m) => ({ default: m.AdminHearingsPage })),
);
const AdminPaymentIntegrationsPage = lazy(() =>
  import("./pages/admin/AdminPaymentIntegrations").then((m) => ({
    default: m.AdminPaymentIntegrationsPage,
  })),
);
const AdminPaymentIntegrationPage = lazy(() =>
  import("./pages/admin/AdminPaymentIntegrations").then((m) => ({
    default: m.AdminPaymentIntegrationPage,
  })),
);
const AdminPaymentSettingsPage = lazy(() =>
  import("./pages/admin/AdminPaymentSettings").then((m) => ({
    default: m.AdminPaymentSettingsPage,
  })),
);
const AdminHearingDesignPage = lazy(() =>
  import("./pages/admin/AdminHearingDesign").then((m) => ({ default: m.AdminHearingDesignPage })),
);
const AdminAuditPage = lazy(() =>
  import("./pages/admin/AdminAudit").then((m) => ({ default: m.AdminAuditPage })),
);

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loading, me } = useAuth();
  if (loading) return <FullSpinner />;
  if (!me?.user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { loading, me } = useAuth();
  if (loading) return <FullSpinner />;
  const isAdmin =
    me?.user.roles.includes("admin") ||
    me?.user.roles.includes("super_admin") ||
    me?.user.roles.includes("staff");
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// ヒアリング未回答でも開かせる画面。
// 受付 QR は当日会場で必要になるため、ここで止めると受付が回らない。
// ヒアリングは画面上のバナーと LINE の文言で催促する。
const HEARING_EXEMPT_PATHS: ReadonlyArray<string> = ["/mypage/qr"];

function HearingGate({ children }: { children: React.ReactNode }) {
  const { me } = useAuth();
  const { pathname } = useLocation();
  // 末尾スラッシュ付き (/mypage/qr/) でも同じ画面にマッチするため、
  // 免除判定の前に正規化する。これが無いと URL の書き方次第で弾かれる。
  const path = pathname.replace(/\/+$/, "") || "/";
  // 会員レコードがあり、ヒアリング未完了の場合は強制誘導
  if (
    me?.member &&
    !me.member.hearingCompleted &&
    !HEARING_EXEMPT_PATHS.includes(path)
  ) {
    return <Navigate to="/first-hearing" replace />;
  }
  return <>{children}</>;
}

function FullSpinner() {
  return (
    <div className="flex h-screen items-center justify-center text-tora">
      Loading…
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<FullSpinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/liff" element={<LiffPage />} />

        {/* 規約・方針は**認証の外**に置く。個人情報を預けるかどうかを
            判断するための文書なので、登録前・ログイン前に読めなければ
            意味がない（ヒアリング未完了でも読める必要がある） */}
        <Route
          path="/terms"
          element={
            <LegalLayout>
              <TermsPage />
            </LegalLayout>
          }
        />
        {/* 個人情報保護方針はブランドごと。持たないブランドでは
            ルート自体を作らない（catch-all で / に戻る＝従来どおり） */}
        {PrivacyDoc && (
          <Route
            path="/privacy"
            element={
              <LegalLayout>
                <PrivacyDoc />
              </LegalLayout>
            }
          />
        )}
        <Route
          path="/password-reset"
          element={<PasswordResetRequestPage />}
        />
        <Route
          path="/password-reset/confirm"
          element={<PasswordResetConfirmPage />}
        />

        <Route
          path="/first-hearing"
          element={
            <RequireAuth>
              <FirstHearingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/password-change"
          element={
            <RequireAuth>
              <PasswordChangePage />
            </RequireAuth>
          }
        />

        <Route
          element={
            <RequireAuth>
              <HearingGate>
                <MemberLayout />
              </HearingGate>
            </RequireAuth>
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/mine" element={<EventsMinePage />} />
          <Route path="/events/:eventId" element={<EventDetailPage />} />
          <Route path="/contents" element={<ContentsPage />} />
          <Route path="/contents/:contentId" element={<ContentDetailPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/mypage" element={<MyPage />} />
          <Route path="/mypage/profile" element={<MyPageProfileEditPage />} />
          <Route path="/mypage/qr" element={<QrPage />} />
        </Route>

        <Route
          path="/admin/*"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            </RequireAuth>
          }
        >
          <Route index element={<AdminHomePage />} />
          <Route path="members" element={<AdminMembersPage />} />
          <Route path="members/:memberId" element={<AdminMemberDetailPage />} />
          <Route path="events" element={<AdminEventsPage />} />
          <Route path="checkin" element={<AdminCheckinPage />} />
          <Route path="contents" element={<AdminContentsPage />} />
          <Route path="products" element={<AdminProductsPage />} />
          <Route path="notices" element={<AdminNoticesPage />} />
          <Route path="hearings" element={<AdminHearingsPage />} />
          <Route path="hearing-design" element={<AdminHearingDesignPage />} />
          <Route path="settings/payment" element={<AdminPaymentSettingsPage />} />
          <Route path="payment-integrations" element={<AdminPaymentIntegrationsPage />} />
          <Route
            path="payment-integrations/:provider"
            element={<AdminPaymentIntegrationPage />}
          />
          <Route path="audit-logs" element={<AdminAuditPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </AuthProvider>
  );
}
